<template>
  <section class="bg-pale-stone" id="contact" aria-labelledby="contact-heading-t2">
    <div class="max-w-[1400px] mx-auto border-x border-charcoal-blue grid grid-cols-1 md:grid-cols-12">
      <div class="px-4 py-8 sm:px-6 md:p-12 lg:p-20 border-b md:border-b-0 space-y-16" :class="hasSchedule ? 'md:col-span-5 md:border-r border-charcoal-blue' : 'md:col-span-12 border-charcoal-blue'">
        <div>
          <h2 id="contact-heading-t2" class="heading-utilitarian text-5xl font-extrabold mb-12 text-charcoal-blue">{{ $t('public.contactUs') }}</h2>
          <div class="space-y-12">
            <div v-for="c in contacts" :key="c.uuid" class="flex gap-6 group">
              <div class="w-12 h-12 bg-concrete-gray border border-charcoal-blue flex items-center justify-center shrink-0 group-hover:bg-charcoal-blue group-hover:text-white transition-colors">
                <span class="material-symbols-outlined text-3xl" :aria-hidden="true">{{ contactIcon(c.type) }}</span>
              </div>
              <div class="min-w-0">
                <p class="font-mono text-xs rms-t2-accent font-bold mb-2 uppercase">
                  {{ contactSectionLabel(c.type) }}
                </p>
                <template v-if="isLinkType(c.type) && contactVal(c)">
                  <a
                    :href="contactVal(c)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="min-h-[44px] inline-flex items-center py-2 -my-2 text-xl font-bold leading-tight rms-t2-accent hover:underline break-all focus-visible:outline-none rms-t2-accent-ring rounded"
                  >
                    {{ contactVal(c) }}
                  </a>
                  <p v-if="c.label" class="text-sm text-charcoal-blue/80 mt-0.5">{{ c.label }}</p>
                </template>
                <template v-else-if="c.type === 'email' && contactVal(c)">
                  <a
                    :href="`mailto:${contactVal(c)}`"
                    class="min-h-[44px] inline-flex items-center py-2 -my-2 text-xl font-bold leading-tight rms-t2-accent hover:underline focus-visible:outline-none rms-t2-accent-ring rounded"
                  >
                    {{ contactVal(c) }}
                  </a>
                  <p v-if="c.label" class="text-sm text-charcoal-blue/80 mt-0.5">{{ c.label }}</p>
                </template>
                <template v-else-if="c.type === 'whatsapp' && whatsAppUrl(contactVal(c))">
                  <a
                    :href="whatsAppUrl(contactVal(c))"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="min-h-[44px] inline-flex items-center py-2 -my-2 text-xl font-bold leading-tight rms-t2-accent hover:underline focus-visible:outline-none rms-t2-accent-ring rounded"
                  >
                    {{ contactVal(c) }}
                  </a>
                  <p v-if="c.label" class="text-sm text-charcoal-blue/80 mt-0.5">{{ c.label }}</p>
                </template>
                <template v-else-if="isCallable(c.type)">
                  <a
                    :href="`tel:${contactVal(c).replace(/\s/g, '')}`"
                    class="min-h-[44px] inline-flex items-center py-2 -my-2 text-xl font-bold leading-tight rms-t2-accent hover:underline focus-visible:outline-none rms-t2-accent-ring rounded"
                  >
                    {{ contactVal(c) }}
                  </a>
                  <p v-if="c.label" class="text-sm text-charcoal-blue/80 mt-0.5">{{ c.label }}</p>
                </template>
                <template v-else>
                  <p class="text-xl font-bold leading-tight text-charcoal-blue">{{ contactVal(c) }}</p>
                  <p v-if="c.label" class="text-sm text-charcoal-blue/80 mt-0.5">{{ c.label }}</p>
                </template>
              </div>
            </div>
            <p v-if="!contacts.length" class="text-charcoal-blue/70">No contact numbers or links listed.</p>
          </div>
        </div>
      </div>
      <div v-if="hasSchedule" id="hours" class="md:col-span-7 px-4 py-8 sm:px-6 md:p-12 lg:p-20 bg-white space-y-12">
        <h2 class="heading-utilitarian text-5xl font-extrabold text-charcoal-blue">{{ $t('public.openingHours') }}</h2>
        <PublicOpenStatusBadge :operating-hours="operatingHours" variant="contact" />
        <div class="grid grid-cols-1 gap-4">
          <template v-if="hoursSummary.is24_7">
            <div
              class="inline-flex min-h-[44px] items-center gap-3 rounded rms-t2-accent-bg px-5 py-2.5"
              role="status"
              :aria-label="$t('public.open24_7')"
            >
              <span class="material-symbols-outlined text-2xl text-white shrink-0" aria-hidden="true">schedule</span>
              <span class="font-mono text-xl font-bold text-white">{{ $t('public.open24_7') }}</span>
            </div>
          </template>
          <template v-else>
            <div
              v-for="row in hoursSummary.perDay"
              :key="row.day"
              class="flex justify-between items-center border-b-2 border-concrete-gray pb-4"
            >
              <span class="heading-utilitarian text-2xl font-bold text-charcoal-blue">{{ $t('public.day' + row.day.charAt(0).toUpperCase() + row.day.slice(1)) }}</span>
              <span class="font-mono text-xl font-bold text-white px-4 py-1" :class="row.text === 'Closed' ? 'bg-charcoal-blue' : 'rms-t2-accent-bg'">{{ row.text === 'Closed' ? $t('public.closedNow') : row.text }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import PublicOpenStatusBadge from '@/components/public/PublicOpenStatusBadge.vue'
import { getOperatingHoursSummary, hasOperatingHoursSchedule } from '@/utils/availability'
import { buildWhatsAppUrl, contactValue, isLinkType } from '@/utils/contact'

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  operatingHours: { type: Object, default: null },
})

const hoursSummary = computed(() => getOperatingHoursSummary(props.operatingHours))
const hasSchedule = computed(() => hasOperatingHoursSchedule(props.operatingHours))

function contactVal(c) {
  return contactValue(c)
}

function contactIcon(type) {
  if (type === 'email') return 'mail'
  if (type === 'whatsapp') return 'chat'
  if (isLinkType(type)) return 'link'
  return 'call'
}

function contactSectionLabel(type) {
  if (type === 'email') return '// Email'
  if (type === 'whatsapp') return '// WhatsApp'
  if (isLinkType(type)) return '// Link'
  return '// Comms Link'
}

function whatsAppUrl(number) {
  return buildWhatsAppUrl(number)
}

function isCallable(type) {
  return ['mobile', 'phone', 'fax'].includes(type)
}
</script>
